import { Link, NavLink, useSearchParams, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import BrandMark from "./icons/BrandMark";
import { useTheme, type ThemeName } from "../lib/theme";

import logoText from "../static/images/logo_text_490x80x96.png"; // or .png /.webp / .avif

type Props = {
    children: React.ReactNode;
};

const THEME_OPTIONS: Array<{ label: string; value: ThemeName | null }> = [
    { label: "System", value: "system" },
    { label: "Default", value: null },
    { label: "Ocean", value: "ocean" },
    { label: "Ember", value: "ember" },
    { label: "Forest", value: "forest" },
    { label: "Paper", value: "paper" },
];

export default function Shell({ children }: Props): JSX.Element {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const isStandalone =
        location.pathname.startsWith("/p/") ||
        searchParams.get("mode") === "standalone";

    const { theme, setTheme } = useTheme();

    const navBtnClass =
        "rounded-xl px-3 py-2 text-sm font-medium border border-[rgb(var(--border)/0.05)] transition text-[rgb(var(--muted2))] hover:bg-[rgb(var(--surface)/0.05)] hover:text-[rgb(var(--fg))]";

    const themeButtonRef = useRef<HTMLButtonElement | null>(null);
    const themeMenuRef = useRef<HTMLDivElement | null>(null);
    const [themeOpen, setThemeOpen] = useState<boolean>(false);

    const currentTheme: ThemeName | null = theme ?? null;

    const currentThemeLabel = useMemo((): string => {
        const found = THEME_OPTIONS.find((t) => t.value === currentTheme);
        return found ? found.label : "Default";
    }, [currentTheme]);

    useEffect(() => {
        if (!themeOpen) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setThemeOpen(false);
                themeButtonRef.current?.focus();
            }
        };

        const onPointerDown = (e: PointerEvent) => {
            const t = e.target as Node | null;
            if (!t) return;

            const btn = themeButtonRef.current;
            const menu = themeMenuRef.current;

            if (btn && btn.contains(t)) return;
            if (menu && menu.contains(t)) return;

            setThemeOpen(false);
        };

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("pointerdown", onPointerDown);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("pointerdown", onPointerDown);
        };
    }, [themeOpen]);

    return (
        <div className="min-h-dvh">
            {!isStandalone && (
                <header className="sticky top-0 z-50 border-b border-[rgb(var(--border)/0.10)] bg-[rgb(var(--bg)/0.70)] backdrop-blur">
                    <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
                        <Link
                            to="/"
                            className="group flex items-center gap-3 rounded-xl px-2 py-1 transition hover:bg-[rgb(var(--surface)/0.05)]"
                        >
                            <BrandMark className="h-9 w-9" />
                            <div className="leading-tight">
                                <img
                                    src={logoText}
                                    alt="Give Me A Visual"
                                    width={490}
                                    height={80}
                                    className="h-6 w-auto"
                                />
                                <div className="text-xs text-[rgb(var(--muted))]">
                                    &nbsp;givemeavisual.com
                                </div>
                            </div>
                        </Link>

                        <div className="flex items-center gap-2">
                            <label className="relative sm:hidden">
                                <span className="sr-only">Menu</span>
                                <select
                                    className="appearance-none rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.50)] px-4 py-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:border-[rgb(var(--border)/0.20)] focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                                    defaultValue=""
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (!v) return;
                                        window.location.href = v;
                                    }}
                                >
                                    <option value="" disabled>
                                        Menu
                                    </option>
                                    <option value="/">Home</option>
                                    <option value="/properties">
                                        Properties
                                    </option>
                                    <option value="/contact">Contact</option>
                                </select>
                                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(var(--muted))]">
                                    ▾
                                </span>
                            </label>

                            <div className="relative">
                                <button
                                    ref={themeButtonRef}
                                    type="button"
                                    className={`${navBtnClass} inline-flex items-center gap-2`}
                                    aria-haspopup="listbox"
                                    aria-expanded={themeOpen}
                                    onClick={() => setThemeOpen((v) => !v)}
                                >
                                    <span className="whitespace-nowrap">
                                        Theme: {currentThemeLabel}
                                    </span>
                                    <span className="text-[rgb(var(--muted))]">
                                        ▾
                                    </span>
                                </button>

                                {themeOpen ? (
                                    <div
                                        ref={themeMenuRef}
                                        role="listbox"
                                        aria-label="Theme"
                                        className="absolute right-0 mt-2 w-44 overflow-hidden rounded-2xl border border-[rgb(var(--border)/0.14)] bg-[rgb(var(--panel)/0.92)] shadow-lg backdrop-blur"
                                    >
                                        <div className="p-1">
                                            {THEME_OPTIONS.map((opt) => {
                                                const active =
                                                    opt.value === currentTheme;
                                                return (
                                                    <button
                                                        key={opt.label}
                                                        type="button"
                                                        role="option"
                                                        aria-selected={active}
                                                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition ${
                                                            active
                                                                ? "bg-[rgb(var(--surface)/0.10)] text-[rgb(var(--fg))]"
                                                                : "text-[rgb(var(--muted2))] hover:bg-[rgb(var(--surface)/0.07)] hover:text-[rgb(var(--fg))]"
                                                        }`}
                                                        onClick={() => {
                                                            setTheme(opt.value);
                                                            setThemeOpen(false);
                                                            themeButtonRef.current?.focus();
                                                        }}
                                                    >
                                                        <span>{opt.label}</span>
                                                        {active ? (
                                                            <span className="text-[rgb(var(--accent))]">
                                                                ●
                                                            </span>
                                                        ) : (
                                                            <span className="text-[rgb(var(--muted3))]">
                                                                {" "}
                                                            </span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <nav className="hidden items-center gap-1 sm:flex">
                                <NavLink
                                    to="/"
                                    className={({ isActive }) =>
                                        [
                                            "rounded-xl px-3 py-2 text-sm font-medium transition",
                                            isActive
                                                ? "bg-[rgb(var(--surface)/0.10)] text-[rgb(var(--fg))]"
                                                : "text-[rgb(var(--muted2))] hover:bg-[rgb(var(--surface)/0.05)] hover:text-[rgb(var(--fg))]",
                                        ].join(" ")
                                    }
                                    end
                                >
                                    Home
                                </NavLink>

                                <NavLink
                                    to="/properties"
                                    className={({ isActive }) =>
                                        [
                                            "rounded-xl px-3 py-2 text-sm font-medium transition",
                                            isActive
                                                ? "bg-[rgb(var(--surface)/0.10)] text-[rgb(var(--fg))]"
                                                : "text-[rgb(var(--muted2))] hover:bg-[rgb(var(--surface)/0.05)] hover:text-[rgb(var(--fg))]",
                                        ].join(" ")
                                    }
                                >
                                    Properties
                                </NavLink>

                                <NavLink
                                    to="/contact"
                                    className={({ isActive }) =>
                                        [
                                            "rounded-xl px-3 py-2 text-sm font-medium transition",
                                            isActive
                                                ? "bg-[rgb(var(--surface)/0.10)] text-[rgb(var(--fg))]"
                                                : "text-[rgb(var(--muted2))] hover:bg-[rgb(var(--surface)/0.05)] hover:text-[rgb(var(--fg))]",
                                        ].join(" ")
                                    }
                                >
                                    Contact
                                </NavLink>
                            </nav>
                        </div>
                    </div>
                </header>
            )}

            <main className="mx-auto w-full max-w-6xl px-4 py-10">
                {children}
            </main>

            {!isStandalone && (
                <footer className="border-t border-[rgb(var(--border)/0.10)]">
                    <div className="mx-auto max-w-6xl px-4 py-8">
                        <div className="grid gap-6 md:grid-cols-3">
                            <div className="space-y-2">
                                <div className="text-sm font-semibold">
                                    Give Me A Visual
                                </div>
                                <div className="text-sm text-[rgb(var(--muted))]">
                                    A product of Snake River Labs, LLC.
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="text-sm font-semibold">
                                    Quick links
                                </div>
                                <div className="flex flex-col gap-2 text-sm">
                                    <Link
                                        to="/"
                                        className="text-[rgb(var(--muted2))] hover:text-[rgb(var(--fg))]"
                                    >
                                        Home
                                    </Link>
                                    <Link
                                        to="/properties"
                                        className="text-[rgb(var(--muted2))] hover:text-[rgb(var(--fg))]"
                                    >
                                        Properties
                                    </Link>
                                    <Link
                                        to="/contact"
                                        className="text-[rgb(var(--muted2))] hover:text-[rgb(var(--fg))]"
                                    >
                                        Contact
                                    </Link>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="text-sm font-semibold">
                                    Contact
                                </div>
                                <div className="text-sm text-[rgb(var(--muted))]">
                                    Email:{" "}
                                    <a
                                        href="mailto:info@givemeavisual.com"
                                        className="text-[rgb(var(--muted2))] hover:text-[rgb(var(--fg))]"
                                    >
                                        info@givemeavisual.com
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-[rgb(var(--border)/0.10)] py-6 text-center text-xs text-[rgb(var(--muted3))]">
                        © 2025-{new Date().getFullYear()} Snake River Labs, LLC.
                        GiveMeAVisual® is a product of Snake River Labs, LLC.
                        <br />
                        Information on this site is provided as-is, without
                        warranties of any kind. Content is for personal
                        reference only and may not be reused or redistributed
                        without permission.
                    </div>
                </footer>
            )}
        </div>
    );
}
